
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {SwapSymbolSettings} from './SwapSymbolSettings';
import {FieldModelSwapSymbolSettings} from './SwapSymbolSettings';

/**
 * SwapSettings struct
 */
class SwapSettings {
  /**
   * Initialize struct
   * @param {!boolean=} isEnabled
   * @param {SwapSymbolSettings=} settings
   * @constructor
   */
  constructor (isEnabled = false, settings = null) {
    this.IsEnabled = isEnabled
    this.Settings = settings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!SwapSettings}
   * @param {!SwapSettings} other Other struct
   * @returns {!SwapSettings} This struct
   */
  copy (other) {
    if (other.IsEnabled != null) {
      this.IsEnabled = other.IsEnabled
    } else {
      this.IsEnabled = null
    }
    if (other.Settings != null) {
      this.Settings = SwapSymbolSettings.fromObject(other.Settings)
    } else {
      this.Settings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!SwapSettings}
   * @returns {!SwapSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new SwapSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new SwapSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!SwapSettings}
   * @param {!SwapSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof SwapSettings)) {
      throw new TypeError('Instance of SwapSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!SwapSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      IsEnabled: ((this.IsEnabled != null) ? this.IsEnabled : null),
      Settings: ((this.Settings != null) ? this.Settings : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return SwapSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!SwapSettings} other Object value
   * @returns {!SwapSettings} Created struct
   */
  static fromObject (other) {
    return new SwapSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!SwapSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return SwapSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!SwapSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 219
  }
}

export { SwapSettings };

/**
 * Fast Binary Encoding SwapSettings field model
 */
class FieldModelSwapSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.IsEnabled = new fbe.FieldModelBool(buffer, 4 + 4)
    this.Settings = new fbe.FieldModelOptional(new FieldModelSwapSymbolSettings(buffer, this.IsEnabled.fbeOffset + this.IsEnabled.fbeSize), buffer, this.IsEnabled.fbeOffset + this.IsEnabled.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelSwapSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelSwapSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.IsEnabled.fbeSize + this.Settings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelSwapSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.IsEnabled.fbeExtra + this.Settings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelSwapSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelSwapSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelSwapSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 219
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelSwapSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelSwapSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IsEnabled.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.IsEnabled.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsEnabled.fbeSize

    if ((fbeCurrentSize + this.Settings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Settings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Settings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelSwapSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelSwapSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelSwapSettings}
   * @param {!SwapSettings} fbeValue Default value, defaults is new SwapSettings()
   * @returns {!SwapSettings} SwapSettings value
   */
  get (fbeValue = new SwapSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelSwapSettings}
   * @param {!SwapSettings} fbeValue SwapSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.IsEnabled.fbeSize) <= fbeStructSize) {
      fbeValue.IsEnabled = this.IsEnabled.get()
    } else {
      fbeValue.IsEnabled = false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.IsEnabled.fbeSize

    if ((fbeCurrentSize + this.Settings.fbeSize) <= fbeStructSize) {
      fbeValue.Settings = this.Settings.get()
    } else {
      fbeValue.Settings = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Settings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelSwapSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelSwapSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelSwapSettings}
   * @param {!SwapSettings} fbeValue SwapSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelSwapSettings}
   * @param {SwapSettings} fbeValue SwapSettings value
   */
  setFields (fbeValue) {
    this.IsEnabled.set(fbeValue.IsEnabled)
    this.Settings.set(fbeValue.Settings)
  }
}

export { FieldModelSwapSettings };

/**
 * Fast Binary Encoding SwapSettings model
 */
class SwapSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelSwapSettings(this.buffer, 4)
  }

  /**
   * Get the SwapSettings model
   * @this {!SwapSettingsModel}
   * @returns {!FieldModelSwapSettings} model SwapSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!SwapSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!SwapSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return SwapSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!SwapSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelSwapSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!SwapSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!SwapSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!SwapSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!SwapSettingsModel}
   * @param {!SwapSettings} value SwapSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!SwapSettingsModel}
   * @param {!SwapSettings} value SwapSettings value, defaults is new SwapSettings()
   * @return {!object} Deserialized SwapSettings value and its size
   */
  deserialize (value = new SwapSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new SwapSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new SwapSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!SwapSettingsModel}
   * @param {!number} prev Previous SwapSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { SwapSettingsModel };
